import { Heart } from 'lucide-react';
import { AppType, PageType } from '../../hooks/useNavigation';
import { Section } from '../../hooks/useSections';
import SearchBox from '../navigation/SearchBox';
import SidebarNav from './SidebarNav';
import { SearchResult } from '../../hooks/useSearch';

interface SidebarProps {
  currentApp: AppType;
  currentPage: PageType;
  mainSections: Section[];
  hiSections: Section[];
  currentSectionId: string | null;
  currentTopicId: string | null;
  goHome: () => void;
  goToSection: (id: string) => void;
  goToTopic: (sectionId: string, topicId: string) => void;
  goHIHome: () => void;
  goToHISection: (id: string) => void;
  goToHITopic: (sectionId: string, topicId: string) => void;
}

// Build nav items from sections (static data since API doesn't return topics list, we use total_topic_count)
// The sidebar needs to know topics per section for expand/collapse.
// Since topics come from the DB, we'll load them lazily or show section-level only.
// For now, sections are navigation entries. Topics load when a section page is viewed.

export default function Sidebar({
  currentApp,
  currentPage,
  mainSections,
  hiSections,
  currentSectionId,
  currentTopicId,
  goHome,
  goToSection,
  goToTopic,
  goHIHome,
  goToHISection,
  goToHITopic,
}: SidebarProps) {
  const handleSearchResult = (result: SearchResult) => {
    const topicId = result.id;
    const sectionId = result.section_id;
    if (currentApp === 'health-insurance') {
      goToHITopic(sectionId, topicId);
    } else {
      goToTopic(sectionId, topicId);
    }
  };

  const navSections = currentApp === 'main'
    ? mainSections.map((s) => ({ id: s.id, title: s.title, icon: s.icon }))
    : hiSections.map((s) => ({ id: s.id, title: s.title, icon: s.icon }));

  return (
    <aside className="w-[280px] bg-white border-l border-content-border flex flex-col flex-shrink-0 overflow-hidden">
      {/* Header */}
      <div className="py-4 px-5 border-b border-content-border">
        <button
          onClick={currentApp === 'health-insurance' ? goHome : goHome}
          className="flex items-center gap-2.5 bg-transparent border-none cursor-pointer py-1 w-full"
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-navy to-oak-green flex items-center justify-center flex-shrink-0">
            <span className="text-white text-base">🌳</span>
          </div>
          <span className="font-bold text-navy text-[15px]">OAK Library</span>
        </button>
      </div>

      {/* Search */}
      <div className="py-3 px-4 border-b border-content-border">
        <SearchBox
          app={currentApp === 'health-insurance' ? 'health-insurance' : 'main'}
          placeholder={currentApp === 'health-insurance' ? 'Search Health Insurance...' : 'Search Core Platform...'}
          onResultClick={handleSearchResult}
        />
      </div>

      {/* HI Home button (only in HI mode) */}
      {currentApp === 'health-insurance' && (
        <div className="py-3 px-4 border-b border-content-border">
          <button
            onClick={goHIHome}
            className={`flex items-center gap-2.5 w-full py-2.5 px-3 rounded-lg cursor-pointer text-left transition-all duration-150 ${
              currentPage.type === 'hi-home'
                ? 'bg-oak-green/[0.15] border-r-[3px] border-r-oak-green'
                : 'border-r-[3px] border-r-transparent'
            }`}
          >
            <Heart
              size={18}
              className={currentPage.type === 'hi-home' ? 'text-oak-green' : 'text-content-secondary'}
            />
            <span
              className={`text-sm ${
                currentPage.type === 'hi-home' ? 'text-oak-green font-semibold' : 'text-content-primary font-normal'
              }`}
            >
              Health Insurance Home
            </span>
          </button>
        </div>
      )}

      {/* Navigation */}
      <SidebarNav
        sections={navSections}
        currentSectionId={currentSectionId}
        currentTopicId={currentTopicId}
        onSectionClick={currentApp === 'health-insurance' ? goToHISection : goToSection}
        onTopicClick={currentApp === 'health-insurance' ? goToHITopic : goToTopic}
      />
    </aside>
  );
}
